import { Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import React, { FC } from "react";
import {
  ChipOptionsDataType,
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  OptionsDataType,
} from "../../../../../components";
import MuiChipWithPopover from "../../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiEllipseLabel from "../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
} from "../../../../../helpers/constants";
import { buildActionMenuItems } from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  ProgramTemplateDeliverablesFormKey,
  ProgramTemplateDeliverablesTabProps,
} from "../../../modelsAndSchemas/Models";
import { createProgramSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

export const pageId = "Create-Program-Template-Activities-tab";

export const CreateProgramTemplateDeliverablesTab: FC<
  ProgramTemplateDeliverablesTabProps
> = ({
  rows,
  control,
  tableRef,
  isViewTemplate,
  uniqueFilters,
  filteredResults,
  onDeliverableViewClick,
  deleteAction,
}) => {
  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const renderFormControl = (
    control: any,
    name: keyof typeof ProgramTemplateDeliverablesFormKey,
    options: OptionsDataType[],
    placeholder: string,
    id: string,
  ) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={options}
        value=""
        id={id}
        placeholder={placeholder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const tableHeaderForm = [
    <FormController
      control={control}
      name={ProgramTemplateDeliverablesFormKey.name}
      key={ProgramTemplateDeliverablesFormKey.name}
    >
      <MuiTextField
        id={`${pageId}-${ProgramTemplateDeliverablesFormKey.name}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFormControl(
      control,
      ProgramTemplateDeliverablesFormKey.deliverableType,
      uniqueFilters.deliverableType,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProgramTemplateDeliverablesFormKey.deliverableType}`,
    ),
    renderFormControl(
      control,
      ProgramTemplateDeliverablesFormKey.activityTypes,
      uniqueFilters.activityTypes,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProgramTemplateDeliverablesFormKey.activityTypes}`,
    ),

    renderFormControl(
      control,
      ProgramTemplateDeliverablesFormKey.units,
      uniqueFilters.units,
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProgramTemplateDeliverablesFormKey.units}`,
    ),
    <></>,
    <></>,
  ];

  const columns = [
    {
      id: ProgramTemplateDeliverablesFormKey.name,
      label: fetchLabel(
        createProgramSiteCoreKeys.createDeliverableKey,
        createProgramSiteCoreKeys.deliverableName,
      ),
      render: (row: any) =>
        !row.name ? null : (
          <MuiLink
            onClick={() =>
              hasModulePermission(
                MODULE_PERMISSIONS.VIEW_ACTIVITIES_AND_DELIVERABLES,
              ) && onDeliverableViewClick(row)
            }
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link"
                text={row.name}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
      isSortVisible: true,
      className: "cell-deliverable-name",
    },
    {
      id: ProgramTemplateDeliverablesFormKey.deliverableType,
      label: fetchLabel(
        createProgramSiteCoreKeys.createDeliverableKey,
        createProgramSiteCoreKeys.deliverableType,
      ),
      isSortVisible: true,
      className: "cell-deliverable-type",
    },
    {
      id: ProgramTemplateDeliverablesFormKey.activityTypes,
      label: fetchLabel(
        createProgramSiteCoreKeys.deliverablesListKey,
        createProgramSiteCoreKeys.deliverablesActivityType,
      ),
      render: (row: any) => (
        <MuiChipWithPopover
          data={
            row.activityTypes.map((item: any) => ({
              label: item.name,
              value: item.id,
            })) as ChipOptionsDataType[]
          }
          handleDelete={() => {}}
          id={"selected-contact-types"}
          isEditable={false}
          headerLabel={fetchLabel(
            createProgramSiteCoreKeys.deliverablesListKey,
            createProgramSiteCoreKeys.deliverablesActivityType,
          )}
          gridRef={React.createRef<HTMLElement>()}
          countToWrap={1}
          showMoreARIALabel={commonARIALabel.showMoreDetails}
          removeChipNameARIALabel={commonARIALabel.removeChipName}
          closePopoverARIALabel={commonARIALabel.closePopover}
        />
      ),
      isSortVisible: false,
      className: "cell-activity-type",
    },
    {
      id: ProgramTemplateDeliverablesFormKey.units,
      label: fetchLabel(
        createProgramSiteCoreKeys.deliverablesListKey,
        createProgramSiteCoreKeys.deliverablesUnits,
      ),
      isSortVisible: true,
      className: "cell-units",
    },
    {
      id: ProgramTemplateDeliverablesFormKey.measurement,
      label: fetchLabel(
        createProgramSiteCoreKeys.deliverablesListKey,
        createProgramSiteCoreKeys.deliverablesMeasurement,
      ),
      isSortVisible: true,
      className: "cell-measurement",
    },
    {
      id: "action",
      label: fetchLabel(
        createProgramSiteCoreKeys.defaultActivityKey,
        createProgramSiteCoreKeys.action,
      ),
      render: (row: any) => {
        const deliverablesActions = [
          {
            permissionKey: MODULE_PERMISSIONS.EDIT_ACTIVITIES_AND_DELIVERABLES,
            create: () => ({
              onClick: () => onDeliverableViewClick(row, true),
              dataTestId: "edit",
              label: commonLabels.edit,
            }),
          },
        ];

        if (!row?.isFrameworkActivityDeliverable) {
          deliverablesActions.push({
            permissionKey:
              MODULE_PERMISSIONS.DELETE_ACTIVITIES_AND_DELIVERABLES,
            create: () => ({
              onClick: () => deleteAction(row),
              dataTestId: "delete-button",
              label: commonLabels.delete,
            }),
          });
        }

        const menuItems = buildActionMenuItems(
          row,
          deliverablesActions,
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  return (
    <MuiGrid container data-testid="search-activities">
      <MuiGrid size={{ xs: 12 }} className="header-right">
        <MuiTable
          ref={tableRef}
          pageId={pageId}
          tableHeaderForm={
            isViewTemplate ? tableHeaderForm.slice(0, -1) : tableHeaderForm
          }
          columns={isViewTemplate ? columns.slice(0, -1) : columns}
          tableResults={[...filteredResults]}
          noDataInApi={rows.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={fetchLabel(
            createProgramSiteCoreKeys.deliverablesListKey,
            createProgramSiteCoreKeys.deliverablesNotAvailable,
          )}
          priorityResults={[]}
          isLastColumnSticky={!isViewTemplate}
        />
      </MuiGrid>
    </MuiGrid>
  );
};
